'use client';

import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDuration } from './coverageFormatters';
import {
  MAX_ACCEPTABLE_GAP_SECONDS,
  type ProjectionVerdict,
  type UnscorableReason,
} from '@/lib/coverageProjection';

/**
 * THE ANSWER, IN THE ROW ABOVE THE BUTTON (PR 3, S7).
 *
 * The question this whole PR exists for is "how many aircraft do I need for this window", and
 * until now the only way to find out was to attach, wait, and read the achieved percentage
 * afterwards. This is that answer before the commit.
 *
 * IT IS AN ESTIMATE AND IT SAYS SO, in three separate ways, because the number is the kind that
 * gets quoted later: the chart beside it carries an ESTIMATE badge, this row names what the
 * estimate does not account for, and any figure it cannot compute is a refusal rather than a zero.
 *
 * A PERCENTAGE IS NEVER SHOWN AS 0 WHEN NOTHING COULD BE COMPUTED. Zero is a measurement and
 * would read as "this rotation covers nothing"; the truth in that case is that the window could
 * not be scored, and the reason names the field to go and fix.
 */

/** What to say for each way the window could not be scored. Each names a field, not a state. */
const UNSCORABLE_SENTENCE: Record<UnscorableReason, string> = {
  // NOT a warning, and not the same remedy: this is the opening state of the wizard, and what it
  // needs is the Aircraft tab, not the calibration flight the sentence below sends people to.
  no_aircraft_picked: 'Pick the aircraft that will fly this rotation to see what it would cover.',
  no_projectable_aircraft:
    'This rotation cannot be scored - none of the aircraft picked can be sized by this platform.',
  station_position_invalid:
    'This rotation cannot be scored - the mission has no usable hold point to measure from.',
  battery_policy_invalid:
    'This rotation cannot be scored - set the battery policy on the Rules step.',
  window_invalid: 'This rotation cannot be scored - the window dates could not be read.',
  window_empty: 'This rotation cannot be scored - the window ends before it starts.',
};

export function CoverageProjectionVerdict({ verdict, targetPct }: {
  verdict: ProjectionVerdict;
  /** The operator's own pass mark, so the figure is judged against what they asked for. */
  targetPct: number | null;
}) {
  if (!verdict.scorable) {
    const nothingPickedYet = verdict.unscorableReason === 'no_aircraft_picked';
    return (
      <p
        data-testid="projection-verdict"
        className={cn(
          'flex items-start gap-1.5 text-[11px]',
          nothingPickedYet ? 'text-muted-foreground' : 'text-status-amber-fg',
        )}
      >
        {/* An empty rotation is not a fault, and an amber warning triangle on the panel's opening
            state is the loudest thing on it. */}
        {!nothingPickedYet && (
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
        )}
        {verdict.unscorableReason === null
          ? 'This rotation cannot be scored.'
          : UNSCORABLE_SENTENCE[verdict.unscorableReason]}
      </p>
    );
  }

  const covered = verdict.coveredPct ?? 0;
  const longest = verdict.longestGapSeconds ?? 0;
  /**
   * BOTH CRITERIA, OR THE HEADLINE LIES BY OMISSION - the rule `CoverageRibbon` already states,
   * and the two disagree in exactly the case this row exists to catch. 99% of window against a 95%
   * target, made of one four-minute hole, passes the percentage and fails the 60-second criterion
   * outright. Grading on the percentage alone would put a green MEETS here and a red FAIL on the
   * same rotation an hour later.
   */
  const meetsTarget = targetPct === null || covered >= targetPct;
  const meetsGap = longest <= MAX_ACCEPTABLE_GAP_SECONDS;
  const passes = meetsTarget && meetsGap;

  return (
    <div data-testid="projection-verdict" className="space-y-1">
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] font-mono tabular-nums">
        <span className={cn('text-sm', passes ? 'text-status-green-fg' : 'text-status-amber-fg')}>
          {covered.toFixed(1)}%
        </span>
        <span className="text-muted-foreground">of the window covered</span>
        {targetPct !== null && (
          <span className={meetsTarget ? 'text-status-green-fg' : 'text-status-amber-fg'}>
            {meetsTarget ? 'MEETS' : 'UNDER'} the {targetPct}% pass mark
          </span>
        )}
        {/* THE GAP CARRIES ITS LIMIT. As a bare duration it was a fact with no judgement attached,
            beside a percentage that had one - so the criterion that actually fails this rotation
            was the only number on the row an operator had to know by heart. */}
        <span className="text-muted-foreground">
          longest gap{' '}
          <span className={cn(meetsGap ? 'text-foreground' : 'text-status-amber-fg')}>
            {longest === 0 ? 'none' : formatDuration(longest)}
          </span>
          {longest > 0 && (
            <span className={meetsGap ? 'text-muted-foreground' : 'text-status-amber-fg'}>
              {' '}against a {MAX_ACCEPTABLE_GAP_SECONDS}s limit
            </span>
          )}
        </span>
      </p>

      {/* WHAT THE FIGURE DOES NOT ACCOUNT FOR. Each of these makes the number OPTIMISTIC, which is
          the direction that leaves a station unmanned, so none of them is optional. */}
      <ul className="text-[10px] text-muted-foreground space-y-0.5">
        {verdict.refusedCount > 0 && (
          <li className="text-status-amber-fg">
            {/* The percentage describes a SMALLER rotation than the one on screen. */}
            It describes {verdict.plans.length - verdict.refusedCount} of the{' '}
            {verdict.plans.length} aircraft picked - the others could not be sized and are left out.
          </li>
        )}
        {verdict.horizonTruncated && (
          <li>
            Measured over {formatDuration(
              (Date.parse(verdict.windowEndsAt) - Date.parse(verdict.windowStartsAt)) / 1000,
            )}, which is as far ahead as this plans.
          </li>
        )}
        {verdict.assumesAllReadyAtStart && (
          <li>
            Assumes every aircraft is ready to launch when the window opens - a dock mid-charge is
            not accounted for.
          </li>
        )}
      </ul>
    </div>
  );
}
